import React, { useState, useEffect } from 'react';

const AnalyticsContent = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalActions: 0,
    totalTokens: 0,
    activeUsers: 0
  });

  // Mock data - replace with actual API calls
  useEffect(() => {
    // Simulate loading data
    setMetrics({
      totalUsers: 1247,
      totalActions: 8934,
      totalTokens: 2456789,
      activeUsers: 342
    });
  }, [timeRange]);

  // Mock chart data
  const userActivityData = [
    { date: '2025-01-08', users: 120, actions: 450, tokens: 120000 },
    { date: '2025-01-09', users: 135, actions: 520, tokens: 135000 },
    { date: '2025-01-10', users: 142, actions: 580, tokens: 142000 },
    { date: '2025-01-11', users: 128, actions: 490, tokens: 128000 },
    { date: '2025-01-12', users: 156, actions: 620, tokens: 156000 },
    { date: '2025-01-13', users: 168, actions: 680, tokens: 168000 },
    { date: '2025-01-14', users: 175, actions: 720, tokens: 175000 },
    { date: '2025-01-15', users: 180, actions: 750, tokens: 180000 }
  ];

  const pageUsageData = [
    { page: 'PDF Parser Smart', users: 342, actions: 2341, tokens: 856432 },
    { page: 'Financial Analysis', users: 198, actions: 1245, tokens: 523456 },
    { page: 'Audit Plan Analysis', users: 156, actions: 892, tokens: 345678 },
    { page: 'RnD Prompting', users: 89, actions: 456, tokens: 234567 },
    { page: 'LLM Configuration', users: 67, actions: 234, tokens: 123456 }
  ];

  const tokenUsageByModel = [
    { model: 'Model A', tokens: 1200000, percentage: 48.8, cost: 245.50 },
    { model: 'Model B', tokens: 856789, percentage: 34.9, cost: 128.50 },
    { model: 'Model C', tokens: 234567, percentage: 9.5, cost: 89.20 },
    { model: 'Model D', tokens: 165433, percentage: 6.7, cost: 45.30 }
  ];

  const topUsers = [
    { user: 'Reza Andreawan', email: 'reza.andreawan@jago.com', actions: 234, tokens: 45678, lastActive: '2 hours ago' },
    { user: 'John Doe', email: 'john.doe@example.com', actions: 189, tokens: 34567, lastActive: '5 hours ago' },
    { user: 'Jane Smith', email: 'jane.smith@example.com', actions: 156, tokens: 28945, lastActive: '1 day ago' },
    { user: 'Bob Johnson', email: 'bob.johnson@example.com', actions: 134, tokens: 23456, lastActive: '2 days ago' },
    { user: 'Alice Brown', email: 'alice.brown@example.com', actions: 98, tokens: 18765, lastActive: '3 days ago' }
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const maxValue = Math.max(...userActivityData.map(d => d.users));

  return (
    <main className="p-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="w-full">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 flex items-center transition-colors duration-300">
                <i className="fa-solid fa-chart-line mr-3 text-blue-600 dark:text-blue-400"></i>
                Analytics Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm transition-colors duration-300">
                Monitor user activity, token usage, and platform metrics
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
                <i className="fa-solid fa-download mr-2"></i>
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-blue-500 dark:border-blue-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 transition-colors duration-300">Total Users</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{formatNumber(metrics.totalUsers)}</p>
                <p className="text-xs text-green-600 mt-2">
                  <i className="fa-solid fa-arrow-up mr-1"></i>
                  +12.5% from last period
                </p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-3 transition-colors duration-300">
                <i className="fa-solid fa-users text-blue-600 dark:text-blue-400 text-2xl transition-colors duration-300"></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-green-500 dark:border-green-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 transition-colors duration-300">Total Actions</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{formatNumber(metrics.totalActions)}</p>
                <p className="text-xs text-green-600 mt-2">
                  <i className="fa-solid fa-arrow-up mr-1"></i>
                  +8.3% from last period
                </p>
              </div>
              <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-3 transition-colors duration-300">
                <i className="fa-solid fa-bolt text-green-600 dark:text-green-400 text-2xl transition-colors duration-300"></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-purple-500 dark:border-purple-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 transition-colors duration-300">Token Usage</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{formatNumber(metrics.totalTokens)}</p>
                <p className="text-xs text-red-600 mt-2">
                  <i className="fa-solid fa-arrow-down mr-1"></i>
                  -3.2% from last period
                </p>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900/30 rounded-full p-3 transition-colors duration-300">
                <i className="fa-solid fa-coins text-purple-600 dark:text-purple-400 text-2xl transition-colors duration-300"></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-orange-500 dark:border-orange-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 transition-colors duration-300">Active Users</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{formatNumber(metrics.activeUsers)}</p>
                <p className="text-xs text-green-600 mt-2">
                  <i className="fa-solid fa-arrow-up mr-1"></i>
                  +15.7% from last period
                </p>
              </div>
              <div className="bg-orange-100 dark:bg-orange-900/30 rounded-full p-3 transition-colors duration-300">
                <i className="fa-solid fa-user-check text-orange-600 dark:text-orange-400 text-2xl transition-colors duration-300"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* User Activity Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 transition-colors duration-300">User Activity Over Time</h3>
            <div className="space-y-3">
              {userActivityData.map((item, index) => {
                const date = new Date(item.date);
                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                const dayNum = date.getDate();
                const barWidth = (item.users / maxValue) * 100;
                
                return (
                  <div key={index} className="flex items-center">
                    <div className="w-16 text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">
                      {dayName} {dayNum}
                    </div>
                    <div className="flex-1 mx-3">
                      <div className="relative h-8 bg-gray-100 rounded">
                        <div
                          className="absolute left-0 top-0 h-full bg-blue-600 rounded flex items-center justify-end pr-2"
                          style={{ width: `${barWidth}%` }}
                        >
                          <span className="text-white text-xs font-medium">{item.users}</span>
                        </div>
                      </div>
                    </div>
                    <div className="w-20 text-xs text-gray-600 text-right">
                      {item.actions} actions
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center space-x-6 text-xs text-gray-600 dark:text-gray-400 transition-colors duration-300">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 dark:bg-blue-400 rounded mr-2 transition-colors duration-300"></div>
                <span>Active Users</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-300 dark:bg-gray-600 rounded mr-2 transition-colors duration-300"></div>
                <span>Total Actions</span>
              </div>
            </div>
          </div>

          {/* Token Usage by Model */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 transition-colors duration-300">Token Usage by Model</h3>
            <div className="space-y-4">
              {tokenUsageByModel.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 transition-colors duration-300 ${
                        index === 0 ? 'bg-blue-600 dark:bg-blue-400' :
                        index === 1 ? 'bg-green-600 dark:bg-green-400' :
                        index === 2 ? 'bg-purple-600 dark:bg-purple-400' : 'bg-orange-600 dark:bg-orange-400'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-800 dark:text-white transition-colors duration-300">{item.model}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-800 dark:text-white transition-colors duration-300">{formatNumber(item.tokens)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">${item.cost.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 transition-colors duration-300">
                    <div
                      className={`h-2 rounded-full transition-colors duration-300 ${
                        index === 0 ? 'bg-blue-600 dark:bg-blue-400' :
                        index === 1 ? 'bg-green-600 dark:bg-green-400' :
                        index === 2 ? 'bg-purple-600 dark:bg-purple-400' : 'bg-orange-600 dark:bg-orange-400'
                      }`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">{item.percentage}% of total</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Page Usage Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">Page Usage Statistics</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 transition-colors duration-300">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Page</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Users</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Actions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Tokens</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {pageUsageData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-300">{item.page}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white transition-colors duration-300">{item.users}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white transition-colors duration-300">{item.actions}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white transition-colors duration-300">{formatNumber(item.tokens)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Users Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">Top Active Users</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 transition-colors duration-300">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Actions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Tokens</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Last Active</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {topUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center transition-colors duration-300">
                            <span className="text-blue-600 dark:text-blue-400 text-xs font-medium transition-colors duration-300">
                              {user.user.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-300">{user.user}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white transition-colors duration-300">{user.actions}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white transition-colors duration-300">{formatNumber(user.tokens)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{user.lastActive}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 transition-colors duration-300">Average Response Time</h4>
            <div className="flex items-end space-x-2">
              <div className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">1.2s</div>
              <div className="text-sm text-green-600 dark:text-green-400 mb-1 transition-colors duration-300">
                <i className="fa-solid fa-arrow-down mr-1"></i>
                15% faster
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden transition-colors duration-300">
              <div className="h-full bg-green-500 dark:bg-green-400 rounded-full transition-colors duration-300" style={{ width: '85%' }}></div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 transition-colors duration-300">Success Rate</h4>
            <div className="flex items-end space-x-2">
              <div className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">98.5%</div>
              <div className="text-sm text-green-600 dark:text-green-400 mb-1 transition-colors duration-300">
                <i className="fa-solid fa-arrow-up mr-1"></i>
                +0.3%
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden transition-colors duration-300">
              <div className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-colors duration-300" style={{ width: '98.5%' }}></div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 transition-colors duration-300">API Calls</h4>
            <div className="flex items-end space-x-2">
              <div className="text-3xl font-bold text-gray-800 dark:text-white transition-colors duration-300">{formatNumber(12456)}</div>
              <div className="text-sm text-green-600 dark:text-green-400 mb-1 transition-colors duration-300">
                <i className="fa-solid fa-arrow-up mr-1"></i>
                +22%
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
              <span className="text-green-600 dark:text-green-400 font-medium transition-colors duration-300">+2,234</span> from last period
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AnalyticsContent;

